import { setupMobX } from '../../../../../util/setupMobX';
import { observable, makeObservable, action, runInAction } from 'mobx';
import { showErrorNotification } from '../../../../../component/notification';
import {
  getVoucherStates,
  redeemVoucher,
} from '../../../../../request/authenticated-requests/voucher-state';

import { VOUCHER_VALUE } from '../components/constants';
import {
  BmsSampleVoucherStatus,
  VoucherAllowanceStatus,
  VoucherStatus,
} from '@web/common';

export class VoucherStateStore {
  @observable public loading: boolean = false;
  @observable public voucherStates: VoucherStatus[] | null = null;
  @observable public bmsSampleVoucherStatus: BmsSampleVoucherStatus = null;
  @observable public loadingRedeem: boolean = false;
  @observable public answeredQuestionnairePoints: number = 0;

  constructor() {
    makeObservable(this);
  }

  @action public resetStore() {
    this.voucherStates = null;
    this.bmsSampleVoucherStatus = null;
    this.answeredQuestionnairePoints = 0;
  }

  @action public loadVoucherStates() {
    if (this.loading) return;

    this.loading = true;

    getVoucherStates()
      .then((res) =>
        runInAction(() => {
          this.voucherStates = res.surveyLinkVoucherStatus;
          this.bmsSampleVoucherStatus = res.bmsSampleVoucherStatus;
          this.answeredQuestionnairePoints = res.questionnaireAnsweredPoints;
        }),
      )
      .catch(() => showErrorNotification())
      .finally(() => runInAction(() => (this.loading = false)));
  }

  @action public redeemVoucher(
    allowanceId: number,
    onSuccess?: () => void,
    onError?: () => void,
  ) {
    if (this.loadingRedeem) return;

    this.loadingRedeem = true;

    redeemVoucher(allowanceId)
      .then(() => {
        this.loadVoucherStates();
        onSuccess?.();
      })
      .catch(() => onError?.())
      .finally(() => runInAction(() => (this.loadingRedeem = false)));
  }

  private getSurveyVoucherUnits(voucherStatus: VoucherStatus): number {
    switch (voucherStatus.voucherStatus.type) {
      case VoucherAllowanceStatus.NoOptionSelected:
        return 0;
      case VoucherAllowanceStatus.Redeemed:
        return voucherStatus.voucherStatus.vouchers.length;
      case VoucherAllowanceStatus.AvailableToBeRedeemed:
      case VoucherAllowanceStatus.Donated:
      case VoucherAllowanceStatus.Taken:
        return 1;
      default:
        return 0;
    }
  }

  // accounts for all vouchers the user unlocked, including redeemed, not redeemed yet and donated
  public get collectedVouchersCount() {
    const surveyCount =
      this.voucherStates?.reduce((acc, item) => {
        return acc + this.getSurveyVoucherUnits(item);
      }, 0) ?? 0;

    // BMS: if status does not exist, no voucher was unlocked.
    // When redeemed we can have multiple vouchers, so count them all; when only available to be redeemed, count a single unlocked unit.
    const bmsCount = !this.bmsSampleVoucherStatus
      ? 0
      : this.bmsSampleVoucherStatus.type === VoucherAllowanceStatus.Redeemed
        ? (this.bmsSampleVoucherStatus.vouchers?.length ?? 0)
        : 1;
    return surveyCount + bmsCount;
  }

  public get availableToBeRedeemedCount() {
    const surveyCount =
      this.voucherStates?.reduce((acc, item) => {
        if (
          item.voucherStatus?.type ===
          VoucherAllowanceStatus.AvailableToBeRedeemed
        ) {
          return acc + this.getSurveyVoucherUnits(item);
        }
        return acc;
      }, 0) ?? 0;

    const bmsCount =
      this.bmsSampleVoucherStatus?.type ===
      VoucherAllowanceStatus.AvailableToBeRedeemed
        ? 1
        : 0;
    return surveyCount + bmsCount;
  }

  public get donatedAmount() {
    const count =
      this.voucherStates?.reduce((acc, item) => {
        if (item.voucherStatus?.type === VoucherAllowanceStatus.Donated) {
          return acc + this.getSurveyVoucherUnits(item);
        }
        return acc;
      }, 0) ?? 0;
    return count * VOUCHER_VALUE;
  }

  public get redeemedCount() {
    const surveyCount =
      this.voucherStates?.reduce((acc, item) => {
        if (item.voucherStatus?.type === VoucherAllowanceStatus.Redeemed) {
          return acc + this.getSurveyVoucherUnits(item);
        }
        return acc;
      }, 0) ?? 0;

    const bmsCount =
      this.bmsSampleVoucherStatus?.type === VoucherAllowanceStatus.Redeemed
        ? this.bmsSampleVoucherStatus.vouchers.length
        : 0;
    return surveyCount + bmsCount;
  }

  public get redeemedAmount() {
    return this.redeemedCount * VOUCHER_VALUE;
  }

  // total amount of collected vouchers (redeemed + available to be redeemed + donated)
  public get collectedAmount() {
    return this.collectedVouchersCount * VOUCHER_VALUE;
  }

  // amount of redeemed vouchers + amount of available to be redeemed vouchers
  public get redeemableAmount() {
    const total = this.availableToBeRedeemedCount + this.redeemedCount;
    return total * VOUCHER_VALUE;
  }

  public get availableToBeRedeemedAmount() {
    return this.availableToBeRedeemedCount * VOUCHER_VALUE;
  }
}

const { provider, useStore } = setupMobX<VoucherStateStore>();
export const useVoucherStateStore = useStore;
export const VoucherStateStoreProvider = provider;
